import * as React from 'react';
import {
  ConnectDragPreview,
  ConnectDragSource,
  DragSource,
  DragSourceConnector,
  DragSourceMonitor,
} from 'react-dnd';
import { Duration, Instant } from 'js-joda';
import { getEmptyImage } from 'react-dnd-html5-backend';
import Hammer from 'hammerjs';
import clsx from 'clsx';

import * as Service from 'types/service';
import { DisplayTime } from '@tock/time';
import { DndItemTypes } from 'constants/dndItemTypes';
import { EventAction } from 'utils/analytics/amplitudeEventConstants';
import { IconExclamationCircle, IconGrab, IconPin } from '@tock/icons';
import { ServicePartyDragMode } from 'state/service/ui';
import { SquareCoordinates } from 'actions/CalendarActions';
import { amplitudeSendEvent } from 'utils/analytics/amplitude';
import { formatDuration } from 'utils/timeUtils';
import { getShouldEnablePartyNotifyIndicator, hasPartyLeft } from 'utils/partyUtils';
import { ifClickable } from '@tock/shared/utils/touchUtils';
import { isTouchDevice } from 'utils/touchUtils';
import { oc } from 'ts-optchain';
import { partyHasUnassignedWarning } from 'selectors/service/singlePartyListSelector';
import PartyBodyCondensed from 'components/service/party/PartyBodyCondensed';
import PartyStates from 'constants/partyStateConstants';
import PureBaseComponent from '@tock/shared/components/PureBaseComponent';

/**
 * Locations in the UI where a drag action can be initiated
 */
export type PartyDragSource =
  | 'TABLE'
  | 'PARTY_PANEL'
  | 'TABLE_PANEL'
  | 'PARTY'
  | 'PARTY_GROUP'
  | 'PARTY_MENU'
  | 'TABLE_MENU'
  | 'TIMELINE_BUBBLE';
export interface PartyDragData {
  mode: 'SERVICE';
  id: number;
  fromTableId?: number;
  party: Service.Party;
  dragSource: PartyDragSource;
  extraOffset?: { x: number; y: number };
  noExtraOffset?: boolean;
  onSuccessfulDrop?(): void;
}

/**
 * Dispatch an analytics event to indicate a user started to drag a party
 */
export const trackDragStart = (source: PartyDragSource, location: 'timeline' | 'service') => {
  amplitudeSendEvent(EventAction.PARTY_TABLE_ASSIGNMENT_STARTED, {
    dragSource: source,
    dragLocation: location,
  });
};

export const partySource = {
  beginDrag(props: {
    isCondensed?: boolean;
    /**
     * Identifies the interface element that initiated the drag action.
     * Used for tracking purposes.
     */
    dragSource: PartyDragSource;
    fromTableId?: number;
    fromPopover?: boolean;
    party?: Service.Party;
    onSuccessfulDrop?(): void;
    noExtraOffset?: boolean;
  }): PartyDragData {
    if (!props.party) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return {} as any;
    }

    trackDragStart(props.dragSource, 'service');

    return {
      id: props.party.id,
      mode: 'SERVICE',
      fromTableId: props.fromTableId,
      party: props.party,
      dragSource: props.dragSource,
      extraOffset:
        (!props.isCondensed && props.dragSource === 'PARTY_MENU' && !props.fromPopover) ||
        props.noExtraOffset
          ? undefined
          : {
              x: -240,
              y: -12,
            },
      onSuccessfulDrop: props.onSuccessfulDrop,
    };
  },
  canDrag(props: {
    party?: Service.Party;
    canDragTables?: boolean;
    isReadOnly: boolean;
    dragMode: ServicePartyDragMode;
  }): boolean {
    if (!props.party) {
      return false;
    }
    const preassignModeCanDrag = !(
      (props.party.state === PartyStates.seated ||
        props.party.state === PartyStates.partiallyseated) &&
      props.dragMode === 'ASSIGN'
    );
    return !props.canDragTables && !props.isReadOnly && preassignModeCanDrag;
  },
};

export function collectSource(connect: DragSourceConnector, monitor: DragSourceMonitor) {
  return {
    connectDragSource: connect.dragSource(),
    connectDragPreview: connect.dragPreview(),
    isDragging: monitor.isDragging(),
  };
}

interface DragConnectProps {
  isDragging: boolean;
  connectDragPreview: ConnectDragPreview;
  connectDragSource: ConnectDragSource;
}

export interface PartyProps {
  className?: string;
  dragMode: ServicePartyDragMode;
  dragSource: PartyDragSource;
  hideTableDetails?: boolean;
  isAssignable?: boolean;
  isHD?: boolean;
  expandable?: boolean;
  isSelected: boolean;
  isReadOnly: boolean;
  onSelectParty(party: Service.Party): void;
  onSelectPartyForPreassign?(party: Service.Party): void;
  onTogglePartyPopover(
    party?: Service.Party,
    coordinates?: SquareCoordinates,
    anchorEle?: HTMLDivElement
  ): void;
  party: Service.Party;
  showGuestListNotes?: boolean;
  showGuestListTags?: boolean;
  wasSelected: boolean;
  showUnassignedWarning?: boolean;
}

type Props = PartyProps & DragConnectProps;

class Party extends PureBaseComponent<Props> {
  partyNode: React.RefObject<HTMLDivElement> = React.createRef();
  mc!: HammerManager;

  componentDidMount(): void {
    if (this.props.connectDragPreview) {
      // Use empty image as a drag preview so browsers don't draw it
      // and we can draw whatever we want on the custom drag layer instead.
      this.props.connectDragPreview(getEmptyImage(), {
        // IE fallback: specify that we'd rather screenshot the node
        // when it already knows it's being dragged so we can hide it with CSS.
        captureDraggingState: true,
      });
    }

    if (isTouchDevice()) {
      const { current: refElement } = this.partyNode;

      if (refElement) {
        this.mc = new Hammer.Manager(refElement, { touchAction: 'pan-y' });
        this.mc.add(
          new Hammer.Press({
            event: 'longpress',
            time: 500,
          })
        );
        this.mc.on('longpress', this.handleContextMenu);
      }
    }
  }

  handleClick(): void {
    const { onSelectParty, party } = this.props;
    onSelectParty(party);
  }

  handleDetailsClick(): void {
    const { isAssignable, onSelectPartyForPreassign, party } = this.props;
    if (!isAssignable) {
      this.handleClick();
      return;
    }
    if (
      !onSelectPartyForPreassign ||
      party.state === 'NO_SHOW' ||
      party.state === 'CANCELED' ||
      party.state === 'LEFT'
    ) {
      this.handleClick();
    } else {
      onSelectPartyForPreassign(party);
    }
  }

  renderStatusIndicator(): JSX.Element | JSX.Element[] | null {
    const { hideTableDetails, isAssignable, party, showUnassignedWarning } = this.props;

    const connectDragSource = this.props.connectDragSource || ((node: JSX.Element) => node);

    if (
      hideTableDetails ||
      party.state === PartyStates.left ||
      party.state === PartyStates.noshow ||
      party.state === PartyStates.canceled
    ) {
      return null;
    }

    const isOrder =
      party?.purchase?.ticketType?.variety === 'ORDER_DELIVERY' ||
      party?.purchase?.ticketType?.variety === 'ORDER_PICKUP';
    if (isOrder) {
      return null;
    }

    const isSeated =
      party.state === PartyStates.seated || party.state === PartyStates.partiallyseated;
    const isUnassignedWithWarning = showUnassignedWarning && partyHasUnassignedWarning(party);
    const className = clsx('Party-tableDetails', {
      'is-draggable': party.state !== PartyStates.seated,
      'is-unassigned': isUnassignedWithWarning,
    });
    if (
      party.state === PartyStates.expected ||
      party.state === PartyStates.arrived ||
      party.state === PartyStates.partiallyarrived
    ) {
      if (party.tables.length > 0 && isAssignable) {
        // If they have a table and are expected, they're preassigned
        return connectDragSource(
          <div className={className} onClick={ifClickable(this.handleDetailsClick)}>
            <p className="Party-tableName">{party.tableString}</p>
            {oc(party).visit.pinnedToTable() && (
              <div className="BookingsList-tables-pinned">
                <IconPin />
              </div>
            )}
          </div>
        );
      } else if (party.tables.length > 0 && !isAssignable) {
        return (
          <div className={className} onClick={ifClickable(this.handleDetailsClick)}>
            <p className="Party-tableName">{party.tableString}</p>
            {oc(party).visit.pinnedToTable() && (
              <div className="BookingsList-tables-pinned">
                <IconPin />
              </div>
            )}
          </div>
        );
      } else if (isAssignable) {
        return connectDragSource(
          <div className={className} onClick={ifClickable(this.handleDetailsClick)}>
            <p className="Party-chevron">
              <IconGrab />
            </p>
          </div>
        );
      } else if (isUnassignedWithWarning) {
        return (
          <div className={className} onClick={ifClickable(this.handleDetailsClick)}>
            <IconExclamationCircle color="error" />
          </div>
        );
      } else {
        return null;
      }
    } else if (isSeated) {
      const indicator = [
        <p key={1} className="Party-tableName">
          {party.tableString}
        </p>,
      ];
      if (party.seatedTime) {
        indicator.push(
          <p key={2} className="Party-additionalInfo Party-seatedTime">
            <DisplayTime timeValue={Instant.ofEpochSecond(party.seatedTime, 0)} />
          </p>
        );
      }
      if (isAssignable && party.state === PartyStates.partiallyseated) {
        return connectDragSource(
          <div className={className} onClick={ifClickable(this.handleDetailsClick)}>
            {indicator}
          </div>
        );
      } else {
        return (
          <div className={className} onClick={ifClickable(this.handleDetailsClick)}>
            {indicator}
          </div>
        );
      }
    } else {
      /* patron has left */ const indicator = [
        <p key={1} className="Party-tableName">
          {party.tableString || '—'}
        </p>,
      ];
      if (party.leftTime && party.seatedTime) {
        const seatedDuration = Duration.ofSeconds(party.leftTime - party.seatedTime);
        const duration = formatDuration(seatedDuration);
        return indicator.concat([
          <p key={2} className="Party-additionalInfo Party-seatedDuration">
            {duration}
          </p>,
        ]);
      } else {
        return (
          <div className={className} onClick={ifClickable(this.handleDetailsClick)}>
            {indicator}
          </div>
        );
      }
    }
  }

  handleContextMenu(e: React.MouseEvent | HammerInput) {
    e.preventDefault();
    if (!this.partyNode.current) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const rect: DOMRect = this.partyNode.current!.getBoundingClientRect() as DOMRect;
    const coordinates = {
      openPositionX: rect.x,
      openPositionY: rect.y,
    };
    this.props.onTogglePartyPopover(this.props.party, coordinates, this.partyNode.current);
  }

  shouldEnableNotifyCircle() {
    const { party } = this.props;
    return getShouldEnablePartyNotifyIndicator(party);
  }

  render() {
    const { className, party, isSelected, isHD, showGuestListNotes, showGuestListTags } =
      this.props;

    const shouldEnableNotifyCircle = this.shouldEnableNotifyCircle();

    const potentialClassNames: Dictionary<boolean> = {
      'is-assigned': party.tables.length > 0,
      'is-dragging': this.props.isDragging,
      'is-expandable': !!this.props.expandable,
      'Party-notify': shouldEnableNotifyCircle,
    };

    if (party.stateDescription.borderClassName) {
      potentialClassNames[party.stateDescription.borderClassName] = true;
      potentialClassNames['Party-status-border'] = true;
    }

    const partyClassnames = clsx('Party', className, potentialClassNames);

    const partyBodyClassnames = clsx('Party-body', {
      'is-selected': this.props.isSelected,
      'is-previously-selected': this.props.wasSelected,
    });

    return (
      <div
        className={partyClassnames}
        ref={this.partyNode}
        onContextMenu={isTouchDevice() ? undefined : this.handleContextMenu}
      >
        <div className={partyBodyClassnames}>
          <PartyBodyCondensed
            isSelected={isSelected}
            party={party}
            onClick={this.handleClick}
            showGuestListNotes={showGuestListNotes}
            showGuestListTags={showGuestListTags}
            isHD={isHD}
          />
        </div>

        {!hasPartyLeft(party) && this.renderStatusIndicator()}
      </div>
    );
  }
}

export default DragSource<PartyProps, DragConnectProps>(
  DndItemTypes.PARTY,
  partySource,
  (connect, monitor) => {
    return {
      connectDragSource: connect.dragSource(),
      connectDragPreview: connect.dragPreview(),
      isDragging: monitor.isDragging(),
    };
  }
)(Party);
